import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, NavLink } from 'react-router-dom';
import { DateTime } from 'luxon';
import classNames from 'classnames';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

import AddIcon from '@material-ui/icons/Add';
import AssignmentIcon from '@material-ui/icons/Assignment';

import EditProposalDialog from 'app/components/election/EditProposalDialog';
import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),
        minHeight: '80vh',
        position: 'relative'
    },
    paper: {
        ...paper(theme),
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginRight: {
        marginRight: theme.spacing(3)
    },
    marginBottomSmall: {
        marginBottom: theme.spacing(1)
    },
    proposalsContainer: {
        paddingTop: theme.spacing(3)
    },
    proposal: {
        textAlign: 'center',
        borderRadius: theme.shape.borderRadius,
        boxShadow: '0px 0px 10px 1px #bbbbbb',
        height: '100%',
        paddingTop: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(12),
        position: 'relative',
        transition: theme.transitions.create(),
        cursor: 'pointer',
        '&:hover': {
            boxShadow: '0px 0px 24px -2px #bbbbbb',
        }
    },
    iconContainer: {
        marginBottom: theme.spacing(2)
    },
    title: {
        lineHeight: 1.3
    },
    proposalBottom: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        paddingBottom: theme.spacing(3),
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    badge: {
        position: 'absolute',
        left: '-8px',
        top: '8px',
        borderRadius: '30px',
        padding: '4px 14px',
        color: '#ffffff',
    },
    badgeactive: {
        backgroundColor: theme.palette.primary.main
    },
    badgeaccepted: {
        backgroundColor: theme.palette.success.main
    },
    badgerejected: {
        backgroundColor: theme.palette.error.main
    }
}));

const newProposal = {
    id: 0,
    values: [],
    user_id: 0
}

export default function ViewProposals(props) {

    let params = useParams();
    const classes = useStyles();
    const api = useApi();

    const [ election, setElection ] = useState(null);
    const [ previousProposals, setPreviousProposal ] = useState([]);

    const [ activeProposal, setActiveProposal ] = useState(newProposal);
    const [ copy, setCopy ] = useState(false);
    const [ editProposalDialogOpen, setEditProposalDialogOpen ] = useState(false);

    const [ loading, setLoading ] = useState(false);
    const [ anchorEl, setAnchorEl] = useState(null);

    // Getters ------------------------------------------

    const getElection = () => {
        setLoading(true);

        api.get(`/election/${params.id}/proposal`)
            .then(res => {
                setElection(res.election);
                if (res.previous_proposals) setPreviousProposal(res.previous_proposals);
                console.log(res.previous_proposals)
            }).finally(() => setLoading(false));
    }

    useEffect(() => {
        getElection();
    }, []);

    // Dialog Functions

    const openEditProposalDialog = (proposal, c=false) => {
        setActiveProposal(proposal);
        setEditProposalDialogOpen(true);
        setCopy(c);
    }

    const closeEditProposalDialog = () => {
        setEditProposalDialogOpen(false);
    }

    const closeEditProposalDialogAndRefresh = () => {
        closeEditProposalDialog();
        getElection();
    }

    const editProposalDialogClosed = () => {
        setActiveProposal(newProposal);
        setCopy(false);
        handleClose();
    }

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const badgeStyle = p => {
        return classNames({
            [classes.badge] : true, //always applies
            [classes.badgeaccepted] : (p.status === 'accepted'),
            [classes.badgeactive] : (p.status === 'active'),
            [classes.badgerejected] : (p.status === 'rejected')
        })
    }

    return (
        <div className={classes.container}>
            <Loading isLoading={loading}/>
            {election &&
                <>
                    <Grid
                        container
                        spacing={1}
                        justify="space-between"
                        alignItems="center"
                        className={classes.marginBottom}
                        >
                        <Grid item>
                            <Typography color="secondary" variant="h4" component="h2">
                                {election.formatted_name}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                to={`/proposals`}
                                variant={'outlined'}
                                component={NavLink}
                                color="secondary"
                                >
                                Back to All Proposals
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        spacing={1}
                        justify="space-between"
                        alignItems="center"
                        className={classes.marginBottomSmall}
                        >
                        <Grid item>
                            <Typography color="textSecondary" variant="h5" component="h4">
                                Your Proposals
                            </Typography>
                        </Grid>
                        <Grid item>
                            {previousProposals.length > 0 &&
                                <>
                                    <Button 
                                        aria-controls="previous-proposal-menu" 
                                        aria-haspopup="true" 
                                        onClick={handleClick}
                                        variant="outlined"
                                        className={classes.marginRight}
                                        color="secondary"
                                        >
                                        Previous Proposals
                                    </Button>
                                    <Menu
                                        id="previous-proposal-menu"
                                        anchorEl={anchorEl}
                                        keepMounted
                                        open={Boolean(anchorEl)}
                                        onClose={handleClose}
                                        >
                                        {previousProposals.map(p => (
                                            <MenuItem key={p.id} onClick={() => openEditProposalDialog(p, true)}>{p.title}</MenuItem>
                                        ))}
                                    </Menu>
                                </>
                            }
                            <Tooltip
                                title={(election.proposals && election.proposals.length >= election.proposals_per_user) ? 'You have reached your maximum amount of proposals.' : `Add ${election.button_name}`}
                                >
                                <span>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={<AddIcon/>}
                                        onClick={() => openEditProposalDialog(newProposal)}
                                        disabled={(election.proposals && election.proposals.length >= election.proposals_per_user)}
                                        >
                                        Add {election.button_name}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <Divider />
                    {(election.proposals && election.proposals.length) ? (
                        <Grid container spacing={3} className={classes.proposalsContainer}>
                            {election.proposals.map(proposal => (
                                <Grid item key={proposal.id} xs={12} sm={6} md={4} lg={3}>
                                    <div
                                        className={classes.proposal}
                                        onClick={() => openEditProposalDialog(proposal)}
                                        >
                                        <div className={badgeStyle(proposal)}>
                                            {window.ucFirst(proposal.status)}
                                        </div>
                                        <div className={classes.iconContainer}>
                                            <AssignmentIcon fontSize='large' color="action"/>
                                        </div>
                                        <Typography
                                            variant='h6'
                                            color='secondary'
                                            gutterBottom
                                            className={classes.title}
                                            >
                                            {proposal.title ?? `Proposal ${proposal.id}`}
                                        </Typography>
                                        {proposal.subtitle &&
                                            <Typography variant='body1' color='textSecondary'>
                                                {proposal.subtitle}
                                            </Typography>
                                        }
                                        <div className={classes.proposalBottom}>
                                            <Typography variant='body2' gutterBottom color='textSecondary'>
                                                Proposed On:
                                            </Typography>
                                            <Typography variant='subtitle2' color='textSecondary'>
                                                {DateTime.fromISO(proposal.created_at).toLocaleString(DateTime.DATETIME_MED)}
                                            </Typography>
                                        </div>
                                    </div>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Typography
                            align="center"
                            color="textSecondary"
                            variant="subtitle2"
                            className={classes.empty}
                            >
                            You have not created any proposals.<br/>
                            <Link onClick={() => openEditProposalDialog(newProposal)} color='secondary'>Click Here</Link> to create a new proposal.
                        </Typography>
                    )}
                    <EditProposalDialog
                        election={election}
                        id={activeProposal.id}
                        open={editProposalDialogOpen}
                        onClose={closeEditProposalDialog}
                        onSave={closeEditProposalDialogAndRefresh}
                        onDelete={closeEditProposalDialogAndRefresh}
                        onClosed={editProposalDialogClosed}
                        copy={copy}
                    />
                </>
            }
        </div>
    );
}
